import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { useAllCached, type UseAllCachedOptions } from '../shared/use-all-cached';
import { requireWorkspaceId } from '../shared/workspace';
import { objectTypeKeys, parseObjectType, type ObjectType } from './entity';

/**
 * Object-type reads. Single endpoint right now: list all active types
 * for the workspace. Detail entries live alongside the list — `getXById`
 * doesn't exist on the backend, so we populate each row's detail slot
 * from the list response and resolve by-id reads from cache.
 */

const STALE_TIME = 60_000;
export const OBJECT_TYPES_EMPTY_POLL_INTERVAL_MS = 2_000;

export function getObjectTypesRefetchInterval(types: readonly ObjectType[] | undefined): number | false {
  return (types?.length ?? 0) === 0 ? OBJECT_TYPES_EMPTY_POLL_INTERVAL_MS : false;
}

/** @public */
export async function getObjectTypes(workspaceId: number, qc: QueryClient): Promise<readonly ObjectType[]> {
  const raw = await hydraUiApi.objects.getObjectTypes({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = (raw.object_types ?? []).map(parseObjectType);
  for (const t of parsed) qc.setQueryData(objectTypeKeys.detail(workspaceId, t.id), t);
  return parsed;
}

export const getObjectTypesQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: objectTypeKeys.list(workspaceId, undefined),
    queryFn: () => getObjectTypes(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UseObjectTypesOptions {
  /**
   * Use this id instead of reading the URL. Pages that render inside a Suspense boundary can mount
   * while the URL is still mid-redirect, so a caller that already resolved the workspace passes it in.
   */
  workspaceId?: number;
  queryConfig?: QueryConfig<typeof getObjectTypesQueryOptions>;
}

/** @public */
export const useObjectTypes = ({ workspaceId: overrideWorkspaceId, queryConfig }: UseObjectTypesOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getObjectTypesQueryOptions(workspaceId, qc),
    ...queryConfig,
  });
};

/**
 * The non-suspending form of `useObjectTypes`, for callers that mount before the workspace is known
 * (the side nav, activity feeds). While `workspaceId` is null the read parks on a sentinel key.
 */
export function useObjectTypesOptional(
  workspaceId: number | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: workspaceId != null ? objectTypeKeys.list(workspaceId, undefined) : objectTypeKeys.inactive(),
    queryFn: workspaceId != null ? () => getObjectTypes(workspaceId, qc) : skipToken,
    staleTime: STALE_TIME,
    enabled,
  });
}

/** Poll an initially-empty list until the first object type appears. */
export const useObjectTypesPolling = ({ queryConfig }: UseObjectTypesOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getObjectTypesQueryOptions(workspaceId, qc),
    refetchInterval: ({ state }) => getObjectTypesRefetchInterval(state.data),
    ...queryConfig,
  });
};

/**
 * Store-wide read of every cached object type — mirrors `useSnippetsCached`
 * /`useCampaignsCached`. Returns a stable array; re-renders on any cache event
 * under `object_type.details`. No fetch fires.
 */
/** @public */
/** Every cached objecttype in the workspace. */
export function useObjectTypesCached(): readonly ObjectType[];
/** Every cached objecttype in the workspace, mapped through `select`. */
export function useObjectTypesCached<TSelected>(
  options: Required<UseAllCachedOptions<ObjectType, TSelected>>,
): TSelected;
export function useObjectTypesCached<TSelected>(
  options?: UseAllCachedOptions<ObjectType, TSelected>,
): TSelected | readonly ObjectType[];
export function useObjectTypesCached<TSelected>(
  options?: UseAllCachedOptions<ObjectType, TSelected>,
): TSelected | readonly ObjectType[] {
  const workspaceId = requireWorkspaceId();
  return useAllCached<ObjectType, TSelected>(objectTypeKeys.details(workspaceId), options);
}
